import axios, { AxiosRequestConfig } from "axios";
declare const USERSERVICE: string;

export type IntegrationFlags = {
  microsoft_graph: boolean;
  ldap: boolean;
  sso: boolean;
  email: boolean;
  tenable: boolean;
  qualys: boolean;
};

const DEFAULTS: IntegrationFlags = {
  microsoft_graph: false,
  ldap: true,
  sso: false,
  email: true,
  tenable: false,
  qualys: false,
};

const CACHE_STORAGE_KEY = "integration_settings_cache_v1";

function userServiceBase(): string {
  return String(USERSERVICE || "").replace(/\/+$/, "");
}

function readPersistedCache(): IntegrationFlags | null {
  try {
    const raw = localStorage.getItem(CACHE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return normalizeFlags(parsed);
  } catch {
    return null;
  }
}

function persistCache(flags: IntegrationFlags) {
  try {
    localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(flags));
  } catch {
    /* ignore quota / private mode */
  }
}

function coerceFlag(value: unknown): boolean | undefined {
  if (value === undefined || value === null || value === "") return undefined;
  if (typeof value === "boolean") return value;
  if (value === 1 || value === "1" || value === "true" || value === "on") return true;
  if (value === 0 || value === "0" || value === "false" || value === "off") return false;
  return Boolean(value);
}

/** Walk `{ status, data }` wrappers until integration flags are found. */
function extractFlags(input: unknown): Partial<IntegrationFlags> {
  let cur: any = input;
  for (let depth = 0; depth < 4 && cur && typeof cur === "object"; depth++) {
    const hasFlagKey = (Object.keys(DEFAULTS) as (keyof IntegrationFlags)[]).some((key) =>
      Object.prototype.hasOwnProperty.call(cur, key)
    );
    if (hasFlagKey) {
      const out: Partial<IntegrationFlags> = {};
      (Object.keys(DEFAULTS) as (keyof IntegrationFlags)[]).forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(cur, key)) {
          const parsed = coerceFlag(cur[key]);
          if (parsed !== undefined) out[key] = parsed;
        }
      });
      return out;
    }
    if (cur.data && typeof cur.data === "object") {
      cur = cur.data;
      continue;
    }
    break;
  }
  return {};
}

function normalizeFlags(input: Partial<IntegrationFlags> | Record<string, unknown>): IntegrationFlags {
  const extracted = extractFlags(input);
  const out = { ...DEFAULTS };
  (Object.keys(DEFAULTS) as (keyof IntegrationFlags)[]).forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(extracted, key)) {
      out[key] = Boolean(extracted[key]);
    }
  });
  return out;
}

export default class IntegrationSettingApi {
  private static cache: IntegrationFlags | null = readPersistedCache();

  private requestConfig(): AxiosRequestConfig {
    // Scoped baseURL — do not mutate axios.defaults (avoids cross-request hangs).
    return {
      baseURL: userServiceBase(),
      timeout: 20000,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    };
  }

  static getDefaults(): IntegrationFlags {
    return { ...DEFAULTS };
  }

  static getCached(): IntegrationFlags {
    if (this.cache) {
      return { ...this.cache };
    }
    const persisted = readPersistedCache();
    if (persisted) {
      this.cache = persisted;
      return { ...persisted };
    }
    return { ...DEFAULTS };
  }

  static isEnabled(key: keyof IntegrationFlags): boolean {
    return Boolean(this.getCached()[key]);
  }

  static setCached(flags: Partial<IntegrationFlags> | Record<string, unknown>) {
    const extracted = extractFlags(flags);
    this.cache = normalizeFlags({ ...(this.cache || readPersistedCache() || DEFAULTS), ...extracted });
    persistCache(this.cache);
  }

  /** Re-read memory cache from localStorage (cross-tab sync). */
  static hydrateFromStorage(): IntegrationFlags {
    const persisted = readPersistedCache();
    if (persisted) {
      this.cache = persisted;
    }
    return this.getCached();
  }

  static clearCached() {
    this.cache = null;
    try {
      localStorage.removeItem(CACHE_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }

  async getSettings(): Promise<IntegrationFlags> {
    const response = await axios.get("/integration-settings", this.requestConfig());
    IntegrationSettingApi.setCached(response.data);
    return IntegrationSettingApi.getCached();
  }

  /**
   * Unauthenticated flags for the login page.
   */
  async getPublicSettings(): Promise<IntegrationFlags> {
    const response = await axios.get(
      "/integration-settings/public",
      this.requestConfig()
    );
    IntegrationSettingApi.setCached(response.data);
    return IntegrationSettingApi.getCached();
  }

  /**
   * Partial update — send only the keys you intend to change.
   * Sending the full flag map from a stale UI was overwriting other integrations.
   */
  async saveSettings(flags: Partial<IntegrationFlags>): Promise<IntegrationFlags> {
    const payload: Partial<IntegrationFlags> = {};
    (Object.keys(flags) as (keyof IntegrationFlags)[]).forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(DEFAULTS, key)) {
        payload[key] = Boolean(flags[key]);
      }
    });

    const response = await axios.post(
      "/integration-settings",
      payload,
      this.requestConfig()
    );
    // Prefer server body; fall back to requested payload merged into cache.
    IntegrationSettingApi.setCached(response.data || payload);
    const cached = IntegrationSettingApi.getCached();
    // Ensure the keys we just saved are reflected even if the response shape is odd.
    IntegrationSettingApi.setCached({ ...cached, ...payload });
    return IntegrationSettingApi.getCached();
  }
}
